/**
 * Dashboard Tab - Role-specific dashboard wrapper for embedded mode
 *
 * Shows the same dashboard components as main app, but in embedded context.
 * Uses the session's role — and, for a grant holder, its grant
 * (`dashboardFor`, #1645; both from `useEmbeddedScreenSubject`) — to
 * determine which dashboard to show. With the unified dashboard's preview on
 * (the session's `flags`, #1337) a client, admin or supervisor gets its shell
 * instead, whose six tabs the host strip draws in place of «Обзор».
 */
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { DashboardSkeleton } from '@/components/ui/skeletons';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { logWarn } from '@/lib/logger';
import { useEmbeddedScreenSubject } from '@/contexts/ScreenSubjectContext';
import { dashboardFor } from '@/lib/route-permissions';
import type {
  UnifiedDashboardSearch,
  UnifiedView,
} from '@/components/dashboards/unified/unified-search';

// Lazy load dashboard components (same as main app)
const AgentDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.AgentDashboard }))
);
const SupervisorDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.SupervisorDashboard }))
);
const AdminDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.AdminDashboard }))
);
const ClientDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.ClientDashboard }))
);
const UnifiedDashboard = lazy(() =>
  import('@/components/dashboards/unified/UnifiedDashboard').then((m) => ({
    default: m.UnifiedDashboard,
  }))
);

export interface DashboardTabProps {
  /**
   * The view of the shell that replaces the role dashboard, `null` — none:
   * the strip's one decision (`unifiedViewOf`, made where the strip is
   * drawn). The tab does not make its own, so the strip's tabs and the panel
   * under them cannot disagree.
   */
  unifiedView: UnifiedView | null;
  /** The unified dashboard's part of the iframe's address (#1337). */
  unifiedSearch: UnifiedDashboardSearch;
  onUnifiedSearchChange: (patch: Partial<UnifiedDashboardSearch>) => void;
}

export function DashboardTab({
  unifiedView,
  unifiedSearch,
  onUnifiedSearchChange,
}: DashboardTabProps) {
  const { t } = useTranslation('dashboard');
  const { isLoading: roleLoading, error } = useEmbeddedOrg();
  // The session's role and the iframe's `/access/me` — the one subject the
  // tabs and the assistant are decided by too (review #1678).
  const { globalRole: role, access } = useEmbeddedScreenSubject();

  // Render role-specific dashboard
  const renderDashboard = () => {
    if (roleLoading) {
      return (
        <div data-testid="embedded-dashboard-loading">
          <DashboardSkeleton />
        </div>
      );
    }

    if (unifiedView) {
      return (
        <div data-testid="embedded-dashboard-unified">
          <UnifiedDashboard
            search={unifiedSearch}
            onSearchChange={onUnifiedSearchChange}
            view={unifiedView}
            tabsInHost
          />
        </div>
      );
    }

    // Show dashboard based on role and grant (same as main app)
    switch (dashboardFor(role, access)) {
      case 'client':
        return (
          <div data-testid="embedded-dashboard-client">
            <ClientDashboard />
          </div>
        );
      case 'admin':
        return (
          <div data-testid="embedded-dashboard-admin">
            <AdminDashboard />
          </div>
        );
      case 'agent':
        return (
          <div data-testid="embedded-dashboard-agent">
            <AgentDashboard />
          </div>
        );
      case 'supervisor':
        return (
          <div data-testid="embedded-dashboard-supervisor">
            <SupervisorDashboard />
          </div>
        );
      default: {
        // role is null/undefined/unrecognized. Previously null/undefined
        // silently rendered AgentDashboard as a "graceful degradation" —
        // but that also swallowed genuine EmbeddedOrgContext session
        // errors (network failure, expired session — see `error` above),
        // showing the least-privileged dashboard as if it were a legitimate
        // choice instead of surfacing that role resolution failed. Never
        // guess a role: always disclose the unresolved state.
        logWarn('embedded.role_unresolved', {
          component: 'DashboardTab',
          role,
          error,
        });
        return (
          <div
            data-testid="embedded-dashboard-role-error"
            className="flex h-full items-center justify-center p-8"
          >
            <div className="text-center">
              <AlertTriangle className="h-8 w-8 text-warning mx-auto mb-3" />
              <p className="text-fg font-medium">{t('unableToDetermineRole')}</p>
              <p className="text-sm text-fg-muted mt-2">
                {error ??
                  t(
                    'detectedRoleContactSupport',
                    'Detected role: {{role}}. Please contact support.',
                    { role: role || 'none' }
                  )}
              </p>
            </div>
          </div>
        );
      }
    }
  };

  return <Suspense fallback={<DashboardSkeleton />}>{renderDashboard()}</Suspense>;
}
